import { Directionality } from '@angular/cdk/bidi';
import {
  DOWN_ARROW,
  LEFT_ARROW,
  PAGE_DOWN,
  PAGE_UP,
  RIGHT_ARROW,
  UP_ARROW,
} from '@angular/cdk/keycodes';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { debounceTime, share } from 'rxjs/operators';

import { dragObservable } from './drag-observable';
import { mouseoverRelativeCoordinatesObservable } from './mouseover-relative-coordinates-observable';

@Component({
  selector: 'bm-slider',
  templateUrl: 'bm-slider.component.html',
  styleUrls: ['bm-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Media progress slider
 */
export class BMSliderComponent implements ControlValueAccessor {
  @HostBinding('attr.aria-valuemax')
  @Input()
  public max!: number;

  @HostBinding('attr.aria-valuenow')
  @Input()
  public set value(v: number) {
    this._value = v;
  }

  public get value() {
    return this._value;
  }

  @HostBinding('attr.aria-valuemin')
  @Input()
  public min = 0;

  @HostBinding('attr.tabindex')
  @Input()
  public tabindex = 0;

  @Input()
  @HostBinding('attr.aria-disabled')
  @HostBinding('class.bm-slider--disabled')
  public disabled!: boolean;

  @HostBinding('attr.role') public role = 'slider';

  @Output() public change = new EventEmitter<number>();
  @Output() public dragStart = new EventEmitter<void>();
  @Output() public dragEnd = new EventEmitter<void>();

  public pointerTime!: number;

  @ViewChild('doneBar', { read: ElementRef, static: true })
  private _doneBar!: ElementRef<HTMLElement>;
  @ViewChild('previewBar', { read: ElementRef, static: false })
  private _previewBar!: ElementRef<HTMLElement>;

  private _isDragging = false;
  private _isOver = false;
  private _value!: number;
  private _onChangeCB!: (_: number) => void;
  private _onTouchedCB!: () => void;

  constructor(
    public _elementRef: ElementRef<HTMLElement>,
    // tslint:disable-next-line:no-any
    @Inject(DOCUMENT) private _document: any,
    private _cdr: ChangeDetectorRef,
    private _dir: Directionality,
  ) {}

  public writeValue(v: number) {
    this.value = v;
  }

  public registerOnChange(fn: (_: number) => void) {
    this._onChangeCB = fn;
  }

  public registerOnTouched(fn: () => void) {
    this._onTouchedCB = fn;
  }

  public setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  @HostListener('mouseenter', ['$event'])
  public onMouseenter($event: MouseEvent) {
    if (this.disabled) {
      return;
    }

    this._isOver = true;

    mouseoverRelativeCoordinatesObservable($event, this._document).subscribe({
      next: val => {
        this.pointerTime = val * (this.max || 0);
        const overTime = Math.max(
          0,
          this.getRelativeLeft(this.pointerTime) -
            this.getRelativeLeft(this.value),
        );
        if (
          overTime > 0 &&
          this._previewBar &&
          this._previewBar.nativeElement
        ) {
          const relativeLeft = this.getRelativeLeft(this.value);

          this._previewBar.nativeElement.style.left = `${relativeLeft}px`;
          this._previewBar.nativeElement.style.width = `${overTime}px`;
        }
        this._cdr.markForCheck();
      },
      error: () => {
        this._isOver = false;
      },
      complete: () => {
        this._isOver = false;
        this._cdr.markForCheck();
      },
    });
  }

  @HostListener('click', ['$event'])
  public onClick(e: MouseEvent) {
    if (!this._isDragging) {
      e.preventDefault();
      const { offsetX } = e;
      const { width } = this._elementRef.nativeElement.getBoundingClientRect();
      const percent = offsetX / width;

      this.value = percent * (this.max || 0);

      this.change.emit(this.value);
    }
  }

  @HostListener('focusin')
  public onFocusin() {
    if (this._onTouchedCB) {
      this._onTouchedCB();
    }
  }

  // tslint:disable-next-line:cyclomatic-complexity
  @HostListener('keydown', ['$event'])
  public onKeydown(e: KeyboardEvent) {
    if (this.disabled) {
      return;
    }

    let emitChanges = false;
    if (this.max) {
      const smallStep = (this.max || 1) / 100;
      switch (e.keyCode) {
        case PAGE_DOWN: {
          e.preventDefault();
          this._value = Math.max(0, this._value - 10 * smallStep);
          emitChanges = true;
          break;
        }
        case PAGE_UP: {
          e.preventDefault();
          this._value = Math.min(this.max, this._value + 10 * smallStep);
          emitChanges = true;
          break;
        }

        case DOWN_ARROW: {
          e.preventDefault();
          this._value = Math.max(0, this._value - smallStep);
          emitChanges = true;
          break;
        }
        case UP_ARROW: {
          e.preventDefault();
          this._value = Math.min(this.max, this._value + smallStep);
          emitChanges = true;
          break;
        }

        case LEFT_ARROW: {
          e.preventDefault();
          this._value = Math.min(
            Math.max(
              0,
              this._value +
                (this._dir.value === 'ltr' ? -smallStep : smallStep),
            ),
            this.max,
          );
          emitChanges = true;
          break;
        }

        case RIGHT_ARROW: {
          e.preventDefault();
          this._value = Math.min(
            Math.max(
              0,
              this._value +
                (this._dir.value === 'ltr' ? smallStep : -smallStep),
            ),
            this.max,
          );
          emitChanges = true;
          break;
        }
        default:
      }
    }

    if (emitChanges) {
      if (this._onChangeCB) {
        this._onChangeCB(this.value);
      }

      this.change.emit(this.value);
    }
  }

  public getRelativeLeft(value: number) {
    if (this._elementRef.nativeElement && value && this.max) {
      const { width } = this._elementRef.nativeElement.getBoundingClientRect();
      const relative = value / (this.max - this.min);
      return relative * width;
    }
    return 0;
  }

  public showThumb() {
    return this._isDragging || this._isOver;
  }

  public showPreviewBar() {
    return this._isOver && !this._isDragging;
  }

  public onThumbMousedown($event: MouseEvent) {
    if (this.disabled) {
      return;
    }

    this._cdr.detach();
    this._isDragging = true;

    if (this._onTouchedCB) {
      this._onTouchedCB();
    }

    this.dragStart.emit();

    const dragObservable$ = dragObservable($event, this._document).pipe(
      share(),
    );

    // The two observables with different throttles target components
    // that should update at different rates. The bounced targets the
    // the time tooltip, while the non bounced targets the dragged thumb
    // and bars.
    dragObservable$
      .pipe(debounceTime(15))
      .subscribe(() => this._cdr.detectChanges());

    dragObservable$.subscribe({
      next: val => {
        this._value = val * (this.max || 0);
        const relativeLeft = this.getRelativeLeft(this.value);
        ($event.target as HTMLElement).style.left = `${relativeLeft}px`;
        if (this._doneBar && this._doneBar.nativeElement) {
          this._doneBar.nativeElement.style.width = `${relativeLeft}px`;
        }

        if (this._onChangeCB) {
          this._onChangeCB(Math.min(Math.max(this.min, this.value), this.max));
        }

        this.change.emit(this.value);
      },
      error: () => {
        this._cdr.reattach();
      },
      complete: () => {
        this._cdr.reattach();
        this.dragEnd.emit();

        setTimeout(() => {
          this._isDragging = false;
        }, 10);
      },
    });
  }
}
